import { useState } from 'react';
import { VolumeHigh, VolumeSlash } from 'iconsax-reactjs';
import { cn } from '@/lib/utils';
import { useI18n } from '@/lib/i18n';
import { playSound, SOUND_NAMES, type SoundName } from '@/lib/sound';
import {
  PRICE_REPEAT_OPTIONS,
  readSoundPrefs,
  writeSoundPrefs,
  type AlertKind,
  type SoundPrefs,
} from '@/lib/useAlertSound';

/** Alerts that can make a sound, in the order they appear. */
const KINDS: {
  kind: AlertKind;
  label:
    | 'settings.soundKnot'
    | 'settings.soundKnotPending'
    | 'settings.soundPoint'
    | 'settings.soundPrice';
}[] = [
  { kind: 'price', label: 'settings.soundPrice' },
  { kind: 'knot', label: 'settings.soundKnot' },
  { kind: 'knotPending', label: 'settings.soundKnotPending' },
  { kind: 'point', label: 'settings.soundPoint' },
];

/**
 * Which sound each alert makes.
 *
 * Every change previews itself. A list of names like "chime" and "knock" tells
 * you nothing about what you are choosing, and a sound you have to save and
 * then wait for an alert to hear is a sound nobody configures deliberately.
 */
export function SoundSettings() {
  const { t } = useI18n();
  const [prefs, setPrefs] = useState<SoundPrefs>(readSoundPrefs);

  const save = (next: SoundPrefs) => {
    setPrefs(next);
    writeSoundPrefs(next);
  };

  const choose = (kind: AlertKind, sound: SoundName) => {
    save({ ...prefs, sounds: { ...prefs.sounds, [kind]: sound } });
    playSound(sound, prefs.volume);
  };

  return (
    <section className="glass rounded-2xl p-4">
      <h2 className="flex items-center gap-2 text-sm font-medium">
        <VolumeHigh size={16} variant="Bold" className="text-primary" />
        {t('settings.sounds')}
      </h2>
      <p className="mt-1 text-xs text-muted-foreground">{t('settings.soundsDescription')}</p>

      <div className="mt-4 space-y-3">
        {KINDS.map(({ kind, label }) => (
          <div key={kind}>
            <span className="block text-sm font-medium">{t(label)}</span>
            <div className="mt-1.5 flex flex-wrap gap-1">
              {SOUND_NAMES.map((name) => (
                <button
                  key={name}
                  type="button"
                  onClick={() => choose(kind, name)}
                  aria-pressed={prefs.sounds[kind] === name}
                  className={cn(
                    'rounded-lg px-2.5 py-1.5 text-xs font-medium transition-colors',
                    prefs.sounds[kind] === name
                      ? 'bg-primary text-primary-foreground'
                      : 'bg-elevated text-muted-foreground hover:text-foreground',
                  )}
                >
                  {t(`settings.sound_${name}` as never)}
                </button>
              ))}
            </div>
            {kind === 'price' && (
              <div className="mt-2 flex flex-wrap items-center gap-1">
                <span className="me-1 text-xs text-muted-foreground">{t('settings.priceRepeat')}</span>
                {PRICE_REPEAT_OPTIONS.map((n) => (
                  <button
                    key={n}
                    type="button"
                    onClick={() => save({ ...prefs, priceRepeat: n })}
                    aria-pressed={prefs.priceRepeat === n}
                    className={cn(
                      'rounded-lg px-2.5 py-1.5 text-xs font-medium transition-colors',
                      prefs.priceRepeat === n
                        ? 'bg-primary text-primary-foreground'
                        : 'bg-elevated text-muted-foreground hover:text-foreground',
                    )}
                  >
                    {n === 0 ? t('settings.priceRepeatUntilDismissed') : t('settings.priceRepeatTimes', { n })}
                  </button>
                ))}
              </div>
            )}
          </div>
        ))}

        <div>
          <span className="block text-sm font-medium">{t('settings.soundVolume')}</span>
          <div className="mt-1.5 flex items-center gap-3">
            <VolumeSlash size={15} className="shrink-0 text-muted-foreground" />
            <input
              type="range"
              min={0}
              max={100}
              step={5}
              value={Math.round(prefs.volume * 100)}
              onChange={(e) => save({ ...prefs, volume: Number(e.target.value) / 100 })}
              // Previewed on release rather than on every step: dragging the
              // slider would otherwise fire twenty overlapping notes.
              onPointerUp={() => playSound(prefs.sounds.knot, prefs.volume)}
              onKeyUp={() => playSound(prefs.sounds.knot, prefs.volume)}
              className="h-1.5 w-full max-w-56 accent-[var(--primary)]"
            />
            <VolumeHigh size={15} className="shrink-0 text-muted-foreground" />
            <span className="tabular w-9 shrink-0 text-xs text-muted-foreground">
              {Math.round(prefs.volume * 100)}%
            </span>
          </div>
        </div>
      </div>

      <p className="mt-3 text-[11px] leading-snug text-muted-foreground">
        {t('settings.soundsNote')}
      </p>
    </section>
  );
}
